import React from 'react';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-slate-900 text-white p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-6">Terms Of Service</h1>
        <div className="prose prose-invert">
          <p className="text-gray-400 mb-6">Last Updated: January 11, 2025</p>
          
          <h2 className="text-xl font-bold mb-3">1. Acceptance of Terms</h2>
          <p className="mb-6">
            By accessing or using The Canon ("Service"), you agree to be bound by these Terms of Service ("Terms"). 
            If you disagree with any part of these terms, you may not access the Service.
          </p>
          
          <h2 className="text-xl font-bold mb-3">2. Description of Service</h2>
          <p className="mb-4">
            The Canon is a hip-hop ranking and debate platform where users can:
          </p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>Create and share artist rankings</li>
            <li>Participate in head-to-head artist comparisons ("Face-offs")</li>
            <li>Engage in debates about hip-hop culture</li>
            <li>Contribute to community-driven rankings</li>
          </ul>
          
          <h2 className="text-xl font-bold mb-3">3. User Accounts</h2>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>You must be at least 13 years old to use this Service</li>
            <li>You are responsible for maintaining the confidentiality of your account</li>
            <li>You are responsible for all activities that occur under your account</li>
            <li>You must provide accurate and complete information when creating an account</li>
          </ul>
          
          <h2 className="text-xl font-bold mb-3">4. User Content</h2>
          <p className="mb-4">
            By posting content to The Canon, you:
          </p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>Grant us a non-exclusive, worldwide, royalty-free license to use, display, and distribute your content within the Service</li>
            <li>Represent that you own or have the necessary rights to post the content</li>
            <li>Agree that your rankings and votes may be aggregated into community statistics</li>
            <li>Understand that your content may be visible to other users</li>
          </ul>
          
          <h2 className="text-xl font-bold mb-3">5. Prohibited Uses</h2>
          <p className="mb-4">
            You agree not to:
          </p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>Post content that is hateful, harassing, threatening, or discriminatory</li>
            <li>Impersonate others or misrepresent your affiliation</li>
            <li>Use the Service for any illegal purposes</li>
            <li>Attempt to manipulate rankings through automated means or multiple accounts</li>
            <li>Scrape, copy, or redistribute Service content without permission</li>
            <li>Post spam or commercial content</li>
            <li>Share copyrighted material without authorization</li>
          </ul>
          
          <h2 className="text-xl font-bold mb-3">6. Intellectual Property</h2>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>The Service and its original content (excluding user content) are owned by The Canon</li>
            <li>Artist names, images, and related content are property of their respective owners</li>
            <li>We respect intellectual property rights and will respond to valid DMCA takedown requests</li>
          </ul>
          
          <h2 className="text-xl font-bold mb-3">7. Disclaimers</h2>
          <p className="mb-4">
            THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTY OF ANY KIND. WE SPECIFICALLY DISCLAIM:
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>The accuracy, completeness, or usefulness of any information on the Service</li>
            <li>Any warranties of merchantability or fitness for a particular purpose</li>
            <li>That the Service will be uninterrupted, secure, or error-free</li>
          </ul>
          <p className="mb-6 text-gray-300">
            Rankings and statistics are based on user opinions and should not be considered factual determinations of artistic merit.
          </p>
          
          <h2 className="text-xl font-bold mb-3">8. Limitation of Liability</h2>
          <p className="mb-4">
            TO THE MAXIMUM EXTENT PERMITTED BY LAW:
          </p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>We shall not be liable for any indirect, incidental, special, or consequential damages</li>
            <li>Our total liability shall not exceed $100</li>
            <li>We are not liable for any user content or interactions between users</li>
            <li>We are not liable for any losses resulting from use of the Service</li>
          </ul>
          
          <h2 className="text-xl font-bold mb-3">9. Indemnification</h2>
          <p className="mb-4">
            You agree to indemnify and hold harmless The Canon, its operators, and affiliates from any claims, damages, or expenses arising from:
          </p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>Your use of the Service</li>
            <li>Your violation of these Terms</li>
            <li>Your violation of any rights of another party</li>
          </ul>
          
          <h2 className="text-xl font-bold mb-3">10. Changes to Terms</h2>
          <p className="mb-6">
            We reserve the right to modify these Terms at any time. Continued use of the Service after changes constitutes acceptance of the new Terms.
          </p>
          
          <h2 className="text-xl font-bold mb-3">11. Termination</h2>
          <p className="mb-6">
            We may terminate or suspend your account at any time, without prior notice, for any reason, including violation of these Terms.
          </p>
          
          <h2 className="text-xl font-bold mb-3">12. Governing Law</h2>
          <p className="mb-6">
            These Terms shall be governed by the laws of North Carolina, without regard to conflict of law provisions.
          </p>
          
          <h2 className="text-xl font-bold mb-3">13. DMCA Notice</h2>
          <p className="mb-4">
            If you believe content on The Canon infringes your copyright, please send a DMCA notice to 
          <a href="mailto:info@thecanon.io" className="text-purple-400 hover:text-purple-300 ml-1">info@thecanon.io </a> 
            including:
          </p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
          <li>Identification of the copyrighted work</li>
          <li>Identification of the infringing material</li>
          <li>Your contact information</li>
          <li>A statement of good faith belief</li>
          <li>A statement of accuracy under penalty of perjury</li>
          <li>Your physical or electronic signature</li>
</ul>

<h2 className="text-xl font-bold mb-3">14. Contact Information</h2>

          <h2 className="text-xl font-bold mb-3">13. Contact Information</h2>
          <p className="mb-6">
            For questions about these Terms, contact: <a href="mailto:info@thecanon.io" className="text-purple-400 hover:text-purple-300">info@thecanon.io</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;